import { useEffect, useRef, useState } from "react"
import { useApolloClient, useMutation } from "react-apollo"
import { useSnackbar } from "react-simple-snackbar"
import classNames from "classnames"
import Link from "next/link"
import { LOCAL_TOGGLE_OVERLAY_MUTATION } from "../../apollo-state/graphql/mutations"
import sendMessage from "../send-message/send-message-query-and-mutation"
import convertProfilePicUrl from "../../utils/convert-profile-pic-url"
import {
  cityOf,
  displayName,
  generatedHeadline,
  linkLabel,
  linksOf,
  localTime,
  profileUrl,
  resolveCover,
} from "./lib"
import { REPORT_USER, SAVE_FOUNDER } from "./queries"
import { CoverBand, CoverPicker, ChangeCoverButton } from "./cover"
import {
  AboutEditor,
  BasicsEditor,
  HeadlineEditor,
  IdeaEditor,
  LinksEditor,
  NameEditor,
  PhotoEditor,
  PromptsEditor,
  useIdeaVisibility,
} from "./editors"
import {
  AboutSection,
  BasicsCard,
  BuildingSection,
  FinishCard,
  FitCard,
  JoinCard,
  LockedPreview,
  NestSection,
  PromptsSection,
  SimilarSection,
  VisibilityCard,
} from "./sections"
import { IconBookmark, IconCamera, IconClock, IconGlobe, IconLink, IconMessage, IconMore, IconPin, IconPlus } from "./icons"
import { EditButton, Pill } from "./ui"
import "./founder-page.scss"

const SNACK = {
  position: "bottom-center",
  style: {
    backgroundColor: "#1C1917",
    border: 0,
    borderRadius: "12px",
    color: "#FFFFFF",
    fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    fontSize: "15px",
    fontWeight: 500,
    textAlign: "center",
    boxShadow: "0 12px 26px -14px rgba(28, 25, 23, 0.5)",
  },
  closeStyle: { color: "#FFFFFF" },
}

// "4:12 pm there", updated each minute; client-only so SSR markup matches
const useLocalTime = (timeZone) => {
  const [now, setNow] = useState(null)
  useEffect(() => {
    if (!timeZone) return undefined
    setNow(new Date())
    const t = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(t)
  }, [timeZone])
  return timeZone && now ? localTime(timeZone, now) : null
}

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (e) {
    return false
  }
}

const MoreMenu = ({ user, me }) => {
  const [open, setOpen] = useState(false)
  const [reported, setReported] = useState(false)
  const [report, { loading }] = useMutation(REPORT_USER, { variables: { userId: user.id } })
  const [openSnackbar] = useSnackbar(SNACK)
  const box = useRef(null)
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => box.current && !box.current.contains(e.target) && setOpen(false)
    const onKey = (e) => e.key === "Escape" && setOpen(false)
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])
  return (
    <div className="fp-menu" ref={box}>
      <button
        type="button"
        className="fp-btn fp-btn--outline fp-btn--square"
        aria-label="More: copy link, report"
        aria-expanded={open ? "true" : "false"}
        onClick={() => setOpen(!open)}
      >
        <IconMore />
      </button>
      {open && (
        <div className="fp-menu-list" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              setOpen(false)
              openSnackbar((await copyText(profileUrl(user.id))) ? "Link copied" : profileUrl(user.id))
            }}
          >
            Copy link to this page
          </button>
          {me && (
            <button
              type="button"
              role="menuitem"
              disabled={loading || reported}
              onClick={async () => {
                await report().catch(() => {})
                setReported(true)
                setOpen(false)
                openSnackbar("Thanks. We'll take a look.")
              }}
            >
              {reported ? "Reported" : `Report ${displayName(user.firstName)}`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Message + Save, for visitors (and shown inert in the owner's preview)
const useVisitorActions = (user, me) => {
  const client = useApolloClient()
  const [toggleOverlay] = useMutation(LOCAL_TOGGLE_OVERLAY_MUTATION)
  const saved = Boolean(user.addedBy && user.addedBy.length)
  const [saveFounder, { loading: saving }] = useMutation(SAVE_FOUNDER)
  const [openSnackbar] = useSnackbar(SNACK)
  const [messaging, setMessaging] = useState(false)

  const message = async () => {
    if (!me) return toggleOverlay()
    setMessaging(true)
    try {
      await sendMessage(client, user.id, me, user.profilePic, user.firstName)
    } catch (e) {
      // the chat overlay shows its own errors
    }
    return setMessaging(false)
  }
  const save = async () => {
    if (!me) return toggleOverlay()
    await saveFounder({
      variables: { userId: user.id, loggedInUserId: me.id },
      optimisticResponse: {
        addUser: { __typename: "UserNotMe", id: user.id, addedBy: saved ? [] : [{ __typename: "UserNotMe", id: me.id }] },
      },
    }).catch(() => {})
    return openSnackbar(saved ? `Removed ${displayName(user.firstName)} from Saved` : `Saved. Find ${displayName(user.firstName)} in Saved.`)
  }
  return { message, save, saved, saving, messaging }
}

const ActionButtons = ({ user, me, inert }) => {
  const { message, save, saved, saving, messaging } = useVisitorActions(user, me)
  const name = displayName(user.firstName)
  return (
    <>
      <div className="fp-actions">
        <button type="button" className="fp-btn fp-btn--lg" onClick={message} disabled={inert || messaging}>
          <IconMessage />
          Message {name}
        </button>
        <button
          type="button"
          className={classNames("fp-btn fp-btn--outline fp-btn--lg", { "fp-on": saved })}
          aria-pressed={saved ? "true" : "false"}
          onClick={save}
          disabled={inert || saving}
        >
          <IconBookmark filled={saved} />
          {saved ? "Saved" : "Save"}
        </button>
        {!inert && <MoreMenu user={user} me={me} />}
      </div>
      <div className="fp-sticky" aria-hidden={inert ? "true" : undefined}>
        <button
          type="button"
          className={classNames("fp-btn fp-btn--outline fp-btn--square fp-btn--lg", { "fp-on": saved })}
          aria-label={saved ? `Saved. Remove ${name} from Saved` : `Save ${name}`}
          aria-pressed={saved ? "true" : "false"}
          onClick={save}
          disabled={inert || saving}
        >
          <IconBookmark filled={saved} />
        </button>
        <button type="button" className="fp-btn fp-btn--lg fp-grow" onClick={message} disabled={inert || messaging}>
          <IconMessage />
          Message {name}
        </button>
      </div>
    </>
  )
}

const OwnerBar = ({ mode, setMode, userId }) => {
  const [openSnackbar] = useSnackbar(SNACK)
  return (
    <div className="fp-ownerbar">
      <p className="fp-muted">This is your page. Founders see it when they open your card.</p>
      <div className="fp-row">
        <div className="fp-segment" role="group" aria-label="View">
          <button type="button" aria-pressed={mode === "edit" ? "true" : "false"} onClick={() => setMode("edit")}>
            Edit
          </button>
          <button type="button" aria-pressed={mode === "preview" ? "true" : "false"} onClick={() => setMode("preview")}>
            <span className="fp-only-desktop">Preview as a visitor</span>
            <span className="fp-only-phone">Preview</span>
          </button>
        </div>
        <button
          type="button"
          className="fp-btn fp-btn--outline fp-copy-link"
          aria-label="Copy link to your page"
          onClick={async () => openSnackbar((await copyText(profileUrl(userId))) ? "Link to your page copied" : profileUrl(userId))}
        >
          <IconLink />
          <span className="fp-only-desktop" aria-hidden="true">
            Copy link
          </span>
        </button>
      </div>
    </div>
  )
}

const Links = ({ user }) => {
  const links = linksOf(user)
  if (!links.length) return null
  return (
    <ul className="fp-links">
      {links.map((l) => (
        <li key={l.url}>
          <a href={l.url} target="_blank" rel="noopener noreferrer nofollow ugc">
            {linkLabel(l.url).includes(".") ? <IconGlobe size={16} /> : <IconLink size={16} />}
            {linkLabel(l.url)}
          </a>
        </li>
      ))}
    </ul>
  )
}

// The whole page. `user` is the profile (for the owner, their `me` merged
// over it); `owner` turns on editing.
const FounderPage = ({ user, me, owner }) => {
  const [mode, setMode] = useState("edit")
  const editing = owner && mode === "edit"
  const visitorView = !owner || mode === "preview"
  const [editor, setEditor] = useState(null) // { kind, ... }
  const [coverOpen, setCoverOpen] = useState(false)
  const [coverPreview, setCoverPreview] = useState(null)
  const [photoOpen, setPhotoOpen] = useState(false)
  const time = useLocalTime(user.locationTimezone)
  const { setActive, loading: toggling } = useIdeaVisibility(me || { id: user.id })

  const name = displayName(user.firstName)
  const fullName = owner ? [user.firstName, user.lastName].filter(Boolean).map(displayName).join(" ") : name
  const headline = user.headline || generatedHeadline(user)
  const cover = resolveCover(coverPreview ? { ...user, coverStyle: coverPreview } : user)
  const since = user.createdAt ? new Date(user.createdAt).getFullYear() : null
  const remote = user.location === "Remote"
  const ideas = [...(user.ideas || [])].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
  const is = (kind) => editing && editor && editor.kind === kind
  const open = (kind, extra) => {
    setEditor({ kind, ...extra })
    setCoverOpen(false)
  }
  const close = () => setEditor(null)

  const onChecklist = (key) => {
    const map = { photo: () => setPhotoOpen(true), skills: () => open("basics"), where: () => open("basics") }
    if (map[key]) return map[key]()
    if (key === "idea") return open("namePitch")
    const kinds = { about: "about", headline: "headline", prompts: "prompts", links: "links" }
    open(kinds[key])
    if (typeof window !== "undefined") {
      setTimeout(() => {
        const el = document.querySelector(".fp-editor")
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" })
      }, 50)
    }
    return undefined
  }

  const finish = editing && <FinishCard me={user} onAction={onChecklist} />
  const basics = is("basics") ? (
    <BasicsEditor me={user} onDone={close} />
  ) : (
    <BasicsCard user={user} owner={editing} onEdit={() => open("basics")} />
  )

  return (
    <div className={classNames("fp", { "fp--rail": Boolean(me) })}>
      <div className="fp-inner">
        {owner && <OwnerBar mode={mode} setMode={setMode} userId={user.id} />}

        <div className="fp-cover-wrap">
          <CoverBand cover={cover}>
            {editing && <ChangeCoverButton onClick={() => setCoverOpen(!coverOpen)} />}
          </CoverBand>
          {editing && coverOpen && <CoverPicker me={user} onPreview={setCoverPreview} onClose={() => setCoverOpen(false)} />}
        </div>

        <section className="fp-identity" aria-labelledby="fp-name">
          <div className="fp-id-row">
            <div className="fp-avatar-wrap">
              <img
                className="fp-avatar"
                src={convertProfilePicUrl(user.profilePic, 144, 144)}
                alt={owner ? "Your photo" : `${name}'s photo`}
                width="144"
                height="144"
              />
              {editing && (
                <button type="button" className="fp-avatar-btn" aria-label="Change photo" onClick={() => setPhotoOpen(true)}>
                  <IconCamera />
                </button>
              )}
            </div>
            {visitorView && <ActionButtons user={user} me={me} inert={owner} />}
          </div>

          <div className="fp-id">
            {is("name") ? (
              <NameEditor me={user} onDone={close} />
            ) : (
              <div className="fp-name-row">
                <h1 className="fp-name" id="fp-name">
                  {editing ? fullName : name}
                </h1>
                {user.hasValidCoFounderSubscription && <Pill tone="amber">Premium</Pill>}
                {editing && <EditButton onClick={() => open("name")} />}
              </div>
            )}
            {editing && !is("name") && <p className="fp-note">Other founders see your first name only.</p>}

            {is("headline") ? (
              <HeadlineEditor me={user} onDone={close} />
            ) : editing && !user.headline ? (
              <div className="fp-headline-slot">
                {headline && <p className="fp-headline">{headline}</p>}
                <p className="fp-small fp-muted">
                  {headline ? "We wrote this from your answers. " : ""}
                  <button type="button" className="fp-text-btn fp-text-btn--blue fp-inline" onClick={() => open("headline")}>
                    Write your own headline
                  </button>
                </p>
              </div>
            ) : (
              headline && (
                <div className="fp-row fp-row--top">
                  <p className="fp-headline">{headline}</p>
                  {editing && <EditButton onClick={() => open("headline")} hidden label="Edit headline" />}
                </div>
              )
            )}

            <div className="fp-meta">
              {user.location && (
                <span className="fp-meta-item">
                  {remote ? <IconGlobe size={16} /> : <IconPin size={16} />}
                  {remote ? "Remote" : user.location}
                </span>
              )}
              {time && !remote && (
                <span className="fp-meta-item">
                  <IconClock size={16} />
                  {owner ? `${time} in ${cityOf(user.location)}` : `${time} there`}
                </span>
              )}
              {since && <span className="fp-meta-item">On FounderCloud since {since}</span>}
            </div>

            {is("links") ? (
              <LinksEditor me={user} onDone={close} />
            ) : (
              <div className="fp-row fp-row--wrap">
                <Links user={user} />
                {editing &&
                  (linksOf(user).length ? (
                    <EditButton onClick={() => open("links")} hidden label="Edit links" />
                  ) : (
                    <button type="button" className="fp-dashed-btn" onClick={() => open("links")}>
                      <IconPlus />
                      Add links
                    </button>
                  ))}
              </div>
            )}
          </div>
        </section>

        {!owner && me && <FitCard me={me} user={user} />}
        {!owner && !me && <JoinCard user={user} />}

        {/* signed-out visitors see the page shape but not the details */}
        <div
          className={classNames("fp-cols", { "fp-cols--locked": !me })}
          aria-hidden={!me || undefined}
          inert={!me ? "" : undefined}
        >
          {!me ? (
            <LockedPreview />
          ) : (
            <>
              <div className="fp-main">
                {is("about") ? (
                  <AboutEditor me={user} onDone={close} />
                ) : (
                  <AboutSection user={user} owner={editing} onEdit={() => open("about")} />
                )}
                {is("prompts") ? (
                  <PromptsEditor me={user} startWith={editor.startWith} onDone={close} />
                ) : (
                  <PromptsSection user={user} owner={editing} onEdit={(startWith) => open("prompts", { startWith })} />
                )}
                {is("idea") || is("newIdea") || is("namePitch") ? (
                  <IdeaEditor
                    me={user}
                    idea={editor.idea}
                    fromPitch={
                      editor.kind === "namePitch"
                        ? { elevatorPitch: user.elevatorPitchForStartup, ideaStage: user.ideaStage, ideaIndustry: user.ideaIndustry }
                        : null
                    }
                    onDone={close}
                  />
                ) : (
                  <BuildingSection
                    user={user}
                    owner={editing}
                    ideas={ideas}
                    onEditIdea={(idea) => open("idea", { idea })}
                    onNamePitch={() => open("namePitch")}
                    onNewIdea={() => open("newIdea")}
                    onToggleIdea={(idea) => setActive(idea.id, !idea.isActive)}
                    toggling={toggling}
                  />
                )}
                <NestSection answers={user.nestAnswers} />
                {!owner && <SimilarSection user={user} />}
              </div>

              <aside className="fp-side" aria-label={owner ? "Your page" : `${name} at a glance`}>
                <div className="fp-side-top">
                  {finish}
                  {basics}
                </div>
                {editing && (
                  <div className="fp-side-end">
                    <VisibilityCard me={user} />
                    <p className="fp-small fp-muted fp-side-note">
                      Email, notifications and billing are in{" "}
                      <Link href="/settings">
                        <a className="fp-link-strong">Settings</a>
                      </Link>
                      . Founders you've saved are in{" "}
                      <Link href="/saved">
                        <a className="fp-link-strong">Saved</a>
                      </Link>
                      .
                    </p>
                  </div>
                )}
              </aside>
            </>
          )}
        </div>
      </div>
      {photoOpen && <PhotoEditor onClose={() => setPhotoOpen(false)} />}
    </div>
  )
}

export default FounderPage
